import { Component, OnInit } from '@angular/core';
import { environment } from '../../../environments/environment'
import { PermitsService } from 'app/forests/services/permits.service';
import { WindowRef } from 'app/_services/native-window.service';
import { AdminApiService } from 'app/services/admin-api.service';
import { ModalService } from 'app/shared/modal/modal.service';
import moment from 'moment';
import { ForestDto, SiteSettingsDto } from '@system-shared/classes.dto';
import { ForestsService } from 'app/forests/services/forests.service';
import { ActivatedRoute, Router } from '@angular/router';
import { DatePipe } from '@angular/common';

@Component({
    selector: 'app-admin-permits',
    templateUrl: './permits.component.html',
    styleUrls: ['./permits.scss'],
    providers: [DatePipe],
    standalone: false
})

export class AdminPermitsComponent implements OnInit {
  nativeWindow: any;
  permits: any;
  emailProcessing = false;
  permitHTML = '';
  columns = [{
    label: 'Pur Date',
    key: 'purchaseDate',
    minWidth: '70px'
  }, {
    label: 'Permit #',
    key: 'forestPermitNumber'
  }, {
    label: 'Expires',
    key: 'permitExpireDate',
    minWidth: '70px'
  }, {
    label: 'Product',
    key: 'product'
  }, {
    label: 'Qty',
    key: 'productQuantity'
  }, {
    label: 'UOM',
    key: 'productUnitType'
  }, {
    label: 'Last Name',
    key: 'lastName'
  }, {
    label: 'First Name',
    key: 'firstName'
  }, {
    label: 'Email',
    key: 'emailAddress',
    minWidth: '150px'
  }]
  regionOptions = [{
    label: 'All Regions',
    key: null
  }];
  regionFilter = this.regionOptions[0]
  forestOptions = [{
    label: 'All Forests',
    key: null,
    regionCode: null
  }];
  filteredForestOptions = [];
  forestFilter = this.forestOptions[0];
  tableSubtitle = '';
  cardData = {};
  startDateFilter: string;
  endDateFilter: string;
  isDateFilterEnabled = false;
  private allForest: ForestDto[] = [];
  protected enhancedPermitViewEnabled: boolean = false;

  constructor(
    private readonly permitsService: PermitsService,
    private readonly winRef: WindowRef,
    private readonly adminApiService: AdminApiService,
    private readonly modalService: ModalService,
    private readonly forestsService: ForestsService,
    private readonly router: Router,
    private readonly route: ActivatedRoute,
    private readonly datePipe: DatePipe
    ) {
    this.nativeWindow = winRef.getNativeWindow();
    this.route.data.subscribe(data => {
      if (data['siteSettings']) {
        this.enhancedPermitViewEnabled = data['siteSettings'].find((setting: SiteSettingsDto) => setting.name === 'ENHANCED_PERMIT_COLUMN')?.enabled || false;
      }
    });
  }

  ngOnInit() {
    this.setTableSubtitle()
    this.getForests();
    this.getPermits();

    this.modalService.confirmEvent.subscribe(res => {
      if (res) {
        this.emailPermit(this.modalService.modalData.value.data);
        this.modalService.confirmEvent.next(false);
      }
    });
  }

  getPermits = () => {
    this.adminApiService.getPermits(this.regionFilter.key, this.forestFilter.key, this.startDateFilter, this.endDateFilter).subscribe((data: any) => {
      this.permits = data.permits;
      this.cardData = {
        permitsCountWeek: data.permitsCountWeek,
        permitsCountMonth: data.permitsCountMonth,
        permitsCountYear: data.permitsCountYear
      };
    })
  }

  getForests = () => {
    this.forestsService.getAllByAdmin().subscribe((forests: ForestDto[]) => {
      this.allForest = forests;
      let adminForestOptions = forests.map((forest: ForestDto) => {
        return {
          label: forest.forestNameFormal,
          key: forest.code,
          regionCode: forest.regionCode
        };
      });
      // if they only have access to one forest then choose the one forest by default
      if (adminForestOptions && adminForestOptions.length === 1) {
        this.forestOptions = [...adminForestOptions];
        this.forestFilter = adminForestOptions[0];
      } else {
        this.forestOptions.push(...adminForestOptions);
      }
      this.filteredForestOptions = [...this.forestOptions];

      let adminRegionOptions = [];
      forests.forEach((forest: any) => {
        if (!adminRegionOptions.some((region: any) => region.key == forest.regionCode)) {
          adminRegionOptions.push({
            label: `${(+forest.regionCode)}`,
            key: forest.regionCode
          });
        }
      });
      adminRegionOptions.sort((a, b) => a.key.localeCompare(b.key));
      // if they only have access to one region then choose the one region by default
      if (adminForestOptions && adminForestOptions.length === 1) {
        this.regionOptions = [...adminRegionOptions];
        this.regionFilter = adminRegionOptions[0];
      } else {
        this.regionOptions.push(...adminRegionOptions);
      }
      this.regionOptions = [...this.regionOptions];
    });
  }

  getCellData = (column, row) => {
    let displayValue = row[column.key];

    if (column.friendly) {
      displayValue = row[column.friendly];
    }
    else if (column.key === 'product') {
      displayValue = row[column.key].charAt(0).toUpperCase() + row[column.key].slice(1);
    }
    else if (column.key === 'productUnitType') {
      if (row.productUnitType === 'Cord') {
        displayValue = `Cords`
      }
      if (row.productUnitType === 'CCF') {
        displayValue = `CCF`
      }
    }
    else if (column.key === 'purchaseDate') {
      displayValue = moment.tz(new Date(row[column.key]), 'America/Chicago').format('MM/DD/YYYY');
    }
    else if (column.key === 'permitExpireDate') {
      displayValue = moment(new Date(row[column.key])).format('MM/DD/YYYY');
    }
    else if (column.key === 'forestPermitNumber') {
      displayValue = `E${row[column.key].toString().padStart(5,'0')}`
    }
    return displayValue;
  };

  filterByForest = (event) => {
    this.forestFilter = event
    this.setTableSubtitle();
    this.getPermits();
  }

  filterByRegion = (event) => {
    this.regionFilter = event
    this.forestFilter = this.forestOptions[0];
    this.setTableSubtitle();
    if (event.key === null) {
      this.filteredForestOptions = this.forestOptions;
    } else {
      this.filteredForestOptions = this.forestOptions.filter((forestOption: any) => forestOption.regionCode == null || forestOption.regionCode == this.regionFilter.key);
    }
    this.getPermits();
  }

  toggleDateFilter = (event) => {
    if (this.isDateFilterEnabled) {
      if (this.permits?.length) {
        let earliestDate = this.permits.reduce((a, b) => a.purchaseDate < b.purchaseDate ? a : b).purchaseDate;
        let latestDate = this.permits.reduce((a, b) => a.purchaseDate > b.purchaseDate ? a : b).purchaseDate;
        this.startDateFilter = this.datePipe.transform(earliestDate, 'yyyy-MM-dd');
        this.endDateFilter = this.datePipe.transform(latestDate, 'yyyy-MM-dd');
      }
    } else {
      this.startDateFilter = null;
      this.endDateFilter = null;
    }
    this.getPermits();
  }

  turnOffDateFilter = (event) => {
    this.startDateFilter = null;
    this.endDateFilter = null;
    this.getPermits();
  }

  filterByStartDate = (event) => {
    this.getPermits();
  }

  filterByEndDate = (event) => {
    this.getPermits();
  }

  setTableSubtitle() {
    if (this.regionFilter.key === null && this.forestFilter.key === null) {
      this.tableSubtitle = 'All Regions - All Forests';
    } else if (this.regionFilter.key !== null && this.forestFilter.key === null) {
      this.tableSubtitle = `${this.regionFilter.label} - All Forests`;
    } else {
      this.tableSubtitle = `${this.regionFilter.label} - ${this.forestFilter.label}`;
    }
  }

  emailConfirmationModal(row) {
    const modalConfig = {
      title: 'Confirm',
      titleIcon: '<i class="fas fa-exclamation-triangle"></i>',
      body: 'Are you sure you want to resend this permit to the permittee?',
      confirmButtonText: 'Confirm',
      closeButtonText: 'Cancel & Close',
      data: row,
    };
    this.modalService.modalData.next(modalConfig);
    this.modalService.open(modalConfig);
  }

  emailPermit = (row) => {
    this.emailProcessing = true;
    this.permitsService.emailPDFbyId(row.permitId).subscribe({
      next: () => {
        this.showEmailSuccessModal();
        this.emailProcessing = false;
      }, error: (err) => {
        console.log(err);
        this.showEmailFailModal();
        this.emailProcessing = false;
      }
    })
  }

  showEmailSuccessModal() {
    const modalConfig = {
      title: 'Success!',
      body: 'Your email has been sent successfully',
      icon: '<i class="icon fas fa-check-circle"></i>'
    };
    this.modalService.open(modalConfig);
  }

  showEmailFailModal() {
    const modalConfig = {
      title: 'Error!',
      body: 'Your email has not been sent, due to server error',
      icon: '<i class="icon fas fa-times-circle"></i>'
    };
    this.modalService.open(modalConfig);
  }

  downloadPermit = (row) => {
    // TODO: Make this use permit service http I.E. this.permitsService.getPermitPDF(row.permitId)
    fetch(environment.apiUrl + 'permits/permit-pdf/' + row.permitId, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      }
    })
    .then(res => {
      return res.arrayBuffer();
    })
    .then(res => {
      const blob = new Blob([res], { type: "application/pdf" })
      const url = window.URL || window.webkitURL;
      const link = url.createObjectURL(blob);
      const a = document.createElement("a");
      a.setAttribute("download", "permit.pdf");
      a.setAttribute("href", link);
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
    })
  }

  downloadLoadTags = (row) => {
    fetch(environment.apiUrl + 'permits/load-tags-pdf/' + row.permitId, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      }
    })
    .then(res => {
      return res.arrayBuffer();
    })
    .then(res => {
      const blob = new Blob([res], { type: "application/pdf" })
      const url = window.URL || window.webkitURL;
      const link = url.createObjectURL(blob);
      const a = document.createElement("a");
      a.setAttribute("download", "load_tags.pdf");
      a.setAttribute("href", link);
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
    })
  }

  hasLoadTags(forestCode: string, product: string): boolean {
    if (!forestCode || !product) {
      return false;
    }
    const matchingForest = this.allForest.find(forest => forest.code === forestCode);
    const matchingPermitType = matchingForest?.permitTypes.find((permitType: { type: string; }) => permitType.type === product);
    return matchingPermitType?.has_load_tags;
  }

  navigateToPermitInfo(permit) {
    this.router.navigate([permit.permitId], { relativeTo: this.route });
  }

  showPermitHistory(permit) {
    console.log('showPermitHistory', permit);
  }
}
